import { CommandProps, RawCommands } from '@tiptap/react';
import { LinkOptions } from '@tiptap/extension-link';
import { Link } from './LinkExtension';

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    internalLink: {
      insertLinks: (links: LinkItem[]) => ReturnType;
      removeInternalLink: () => ReturnType;
    };
  }
}

declare module '@tiptap/core' {
  interface Storage {
    internalLink: {
      linkPicker: (() => Promise<LinkItem[]>) | null;
    };
  }
}

export type LinkItem = {
  url: string;
  label: string;
};

type InternalLinkOptions = {
  linkPicker: () => Promise<LinkItem[]>;
} & LinkOptions;

const InternalLinkExtension = Link.extend<InternalLinkOptions>({
  name: 'internalLink',
  inclusive: false,

  addOptions() {
    return {
      ...(this.parent?.() ?? ({} as InternalLinkOptions)),
      linkPicker: null as unknown as () => Promise<LinkItem[]>,
      HTMLAttributes: {
        'data-type': 'internalLink',
      },
      openOnClick: false,
    };
  },

  addStorage() {
    return {
      linkPicker: null,
    };
  },

  onCreate(event) {
    this.parent?.(event);
    this.storage.linkPicker = this.options.linkPicker;
  },

  parseHTML() {
    return [
      {
        tag: 'a[href^="/api/attachment"]',
      },
    ];
  },

  addCommands() {
    return {
      removeInternalLink:
        () =>
        ({ chain }: CommandProps) => {
          chain().focus().unsetMark('internalLink').run();
        },
      insertLinks:
        (links: LinkItem[]) =>
        ({ chain }: CommandProps) => {
          links.map(link => {
            chain()
              .focus()
              .insertContent({
                type: 'text',
                text: link.label,
                marks: [
                  {
                    type: 'internalLink',
                    attrs: {
                      href: link.url,
                      'data-type': 'internalLink',
                      target: '_blank',
                    },
                  },
                ],
              })
              // Couldn't find a better way to update the selection before inserting
              // the space.
              .insertContent(
                { type: 'text', text: '' },
                { updateSelection: true }
              )
              .insertContent({ type: 'text', text: ' ' })
              .run();
          });
        },
    } as Partial<RawCommands>;
  },
});

export { InternalLinkExtension };
